import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Mail, Loader2, Send, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Modal that lets the user pick between their personal email template and the
 * latest AI-generated draft, optionally tweak subject/body/recipient, then
 * opens a pre-filled mailto: link in their default mail client.
 */
export default function OutreachExportDialog({ open, onOpenChange, accountId, accountName, hasAIDraft }) {
  const [type, setType] = useState('custom');       // 'custom' | 'ai'
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch render output every time the dialog opens or the type changes
  useEffect(() => {
    if (!open || !accountId) return undefined;
    let cancelled = false;
    setLoading(true);
    const run = async () => {
      try {
        const res = await api.post('/outreach/render', { template_type: type, account_id: accountId });
        if (cancelled) return;
        const subj = res.data.subject || '';
        const bd = res.data.body || '';
        const recs = res.data.recipients || [];
        setSubject(subj);
        setBody(bd);
        setRecipients(recs);
        const primary = recs.find(r => r.primary);
        setSelectedEmail(primary ? primary.email : (recs[0]?.email || ''));
        setUseCustom(false);
        setCustomEmail('');
      } catch (err) {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || 'Konnte E-Mail nicht aufbereiten');
        setSubject(''); setBody(''); setRecipients([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, accountId, type]);

  const finalEmail = useCustom ? customEmail.trim() : selectedEmail;

  const buildMailto = () => {
    const to = encodeURIComponent(finalEmail || '');
    const s = encodeURIComponent(subject || '');
    const b = encodeURIComponent(body || '');
    return `mailto:${to}?subject=${s}&body=${b}`;
  };

  const handleExport = () => {
    if (!finalEmail) {
      toast.error('Bitte eine Empfänger-E-Mail wählen oder eingeben');
      return;
    }
    if (!subject && !body) {
      toast.error('Betreff und Nachricht sind leer');
      return;
    }
    window.location.href = buildMailto();
    onOpenChange(false);
    toast.success('E-Mail-Programm wird geöffnet');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="outreach-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> E-Mail an {accountName}</DialogTitle>
          <DialogDescription>Wähle eine Vorlage, prüfe Empfänger und öffne deine E-Mail-App.</DialogDescription>
        </DialogHeader>

        {/* Vorlagen-Auswahl */}
        <div className="space-y-2">
          <Label>Vorlage</Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setType('custom')}
              data-testid="select-custom-template"
              className={`px-4 py-3 text-left rounded-lg border-2 transition-all ${type === 'custom' ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-border hover:border-muted-foreground/30'}`}
            >
              <div className="flex items-center gap-2 mb-0.5"><Pencil className="w-4 h-4" /> <span className="font-medium text-sm">Meine Vorlage</span></div>
              <p className="text-xs text-muted-foreground">Dein in den Einstellungen gepflegter Text</p>
            </button>
            <button
              type="button"
              onClick={() => setType('ai')}
              disabled={!hasAIDraft}
              data-testid="select-ai-template"
              className={`px-4 py-3 text-left rounded-lg border-2 transition-all ${type === 'ai' ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-border hover:border-muted-foreground/30'} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <div className="flex items-center gap-2 mb-0.5"><Mail className="w-4 h-4" /> <span className="font-medium text-sm">KI-Vorschlag</span></div>
              <p className="text-xs text-muted-foreground">{hasAIDraft ? 'Personalisierter KI-Text' : 'Erst „Entwurf erstellen"'}</p>
            </button>
          </div>
        </div>

        {/* Empfänger-Auswahl */}
        <div className="space-y-2">
          <Label>Empfänger</Label>
          {recipients.length > 0 && !useCustom && (
            <div className="space-y-1.5">
              {recipients.map((r) => (
                <label key={r.email} className={`flex items-center gap-3 p-2.5 rounded-md border cursor-pointer transition-colors ${selectedEmail === r.email ? 'border-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/20' : 'border-border hover:bg-muted/50'}`}>
                  <input
                    type="radio"
                    name="recipient"
                    value={r.email}
                    checked={selectedEmail === r.email}
                    onChange={() => setSelectedEmail(r.email)}
                    className="accent-emerald-600"
                    data-testid={`recipient-radio-${r.email}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.email}</p>
                    <p className="text-xs text-muted-foreground">{r.label}</p>
                  </div>
                  {r.primary && <Badge variant="secondary" className="text-[10px]">empfohlen</Badge>}
                </label>
              ))}
            </div>
          )}
          {(recipients.length === 0 || useCustom) && (
            <Input
              type="email"
              placeholder="empfaenger@beispiel.de"
              value={customEmail}
              onChange={e => setCustomEmail(e.target.value)}
              data-testid="custom-recipient-input"
            />
          )}
          {recipients.length > 0 && (
            <button
              type="button"
              onClick={() => setUseCustom(v => !v)}
              className="text-xs text-muted-foreground hover:text-foreground underline"
              data-testid="toggle-custom-recipient"
            >
              {useCustom ? '← Aus Liste wählen' : 'Andere Adresse eingeben →'}
            </button>
          )}
          {recipients.length === 0 && !customEmail && (
            <p className="text-xs text-amber-600">Keine E-Mail-Adresse im Lead hinterlegt — bitte manuell eintragen.</p>
          )}
        </div>

        {/* Betreff + Body — editierbar vor dem Export */}
        <div className="space-y-2">
          <Label htmlFor="export-subject">Betreff</Label>
          <Input
            id="export-subject"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            data-testid="export-subject"
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="export-body">Nachricht</Label>
          <textarea
            id="export-body"
            value={body}
            onChange={e => setBody(e.target.value)}
            rows={10}
            disabled={loading}
            data-testid="export-body"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <p className="text-xs text-muted-foreground">
            Letzte Anpassungen kannst du auch noch direkt in deinem Mail-Programm machen.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-export">Abbrechen</Button>
          <Button onClick={handleExport} disabled={loading || !finalEmail} data-testid="open-in-mail-client">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            E-Mail-Programm öffnen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
